import type { TFunction } from 'i18next'
import { getDateFromDate } from '@/utils'
import { WILDCARD, describeScopes } from './scope-catalog'

// A key as the list shows it. The stored identity is (ecommerce_group, name), never the name
// alone: a name is unique only inside its group.
export interface ApiKeyRow {
  group: string
  name: string
  scopes: string[]
  createdAt?: string
  // Empty on keys issued before attribution was recorded and on keys REVER issued, which is not
  // the same as "issued by nobody".
  createdBy: string
  // Unset on keys not used since usage was first recorded.
  lastUsedAt?: string
}

// Who is looking at the page. REVER staff (group "*") can list, create and delete any group's
// keys, but never read an existing value: a key belongs in the hands of the merchant using it.
export type Viewer = 'admin' | 'rever'

// An empty scope list is the legacy wildcard: auths reads "no scopes" as "every scope".
export function accessLabel(scopes: string[], t: TFunction): string {
  if (scopes.length === 0) return t('api_keys.full_access')
  const named = describeScopes(scopes, t)
  if (!scopes.includes(WILDCARD)) return named.join(', ')
  return named.length === 0
    ? t('api_keys.full_access')
    : t('api_keys.full_access_plus', { scopes: named.join(', ') })
}

export function formatCreated(iso: string | undefined, lng: string): string {
  if (!iso) return '—'
  return getDateFromDate(new Date(iso), lng)
}

// The row's "Created" cell. created_by is empty on keys issued before it was recorded, which is
// not the same as knowing who issued them, so the cell says only when.
export function createdLabel(
  key: ApiKeyRow,
  t: TFunction,
  lng: string
): string {
  const date = formatCreated(key.createdAt, lng)
  return key.createdBy
    ? t('api_keys.created_by', { date, name: key.createdBy })
    : date
}

export function lastUsedLabel(
  iso: string | undefined,
  t: TFunction,
  lng: string
): string {
  return iso ? formatCreated(iso, lng) : t('api_keys.never_used')
}

export function findByName(
  keys: ApiKeyRow[],
  group: string,
  name: string
): ApiKeyRow | undefined {
  const needle = name.trim().toLowerCase()
  if (!needle) return undefined
  return keys.find(
    (key) => key.group === group && key.name.toLowerCase() === needle
  )
}
